import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import {
  addWidget,
  createDashboard,
  deleteDashboard,
  deleteWidget,
  fetchDashboard,
  fetchDashboards,
  fetchDefaultDashboard,
  fetchReports,
  refreshWidget,
  saveLayout,
  updateDashboard,
  updateWidget,
} from './api'
import { StateView } from '@/card/record/StateView'
import { LeaderOverview } from './LeaderOverview'
import { moveBefore, packLayout } from './layout'
import { ReportPage, ReportState } from './ReportShell'
import { WidgetCard } from './WidgetView'
import { GRID_COLUMNS, MINIMUM_REFRESH_SECONDS, WIDGET_TYPES } from './types'
import type { Dashboard, DashboardWidget, LoadedDashboard, SaveWidgetRequest, WidgetType } from './types'

/**
 * Дашборды и виджеты (ТЗ §4.6) — тикет #77.
 *
 * <h2>Критерий тикета</h2>
 *
 * «Руководитель собирает дашборд из своих отчётов и видит его при входе, а не идёт в
 * раздел отчётов за каждой цифрой». Отсюда: экран открывается на дашборде **по
 * умолчанию**, а не на списке. Список — это ещё один шаг, а тикет ровно про то, чтобы
 * шагов не было.
 *
 * <h2>Права</h2>
 *
 * Пункт меню закрыт правом `analytics.dashboard:read`, но это только турникет. Какие
 * цифры показать, решает право на **источник отчёта** под виджетом, и проверяет его
 * сервер от имени смотрящего. Клиент рисует то, что ему прислали: виджет с `errorCode`
 * показывается отказом, а не пустой карточкой.
 *
 * Кнопки правки скрыты у того, у кого нет `update`, — но это удобство, а не защита:
 * запрет живёт на сервере, и владение дашбордом он проверяет отдельно от права.
 */
export function DashboardsPage() {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)

  const dashboards = useQuery({
    queryKey: ['dashboards'],
    queryFn: ({ signal }) => fetchDashboards(signal),
  })

  // Дашборд по умолчанию — то, ради чего тикет: вход попадает на него, а не в список.
  const preferred = useQuery({
    queryKey: ['dashboards', 'default'],
    queryFn: ({ signal }) => fetchDefaultDashboard(signal),
  })

  // Выбор делается один раз, при появлении ответа: иначе он перебивал бы ручное
  // переключение при каждом перезапросе, и уйти с дашборда по умолчанию было бы нельзя.
  useEffect(() => {
    if (selected !== null || preferred.isLoading) return
    const fallback = dashboards.data?.[0]?.id ?? null
    setSelected(preferred.data?.id ?? fallback)
  }, [preferred.data, preferred.isLoading, dashboards.data, selected])

  const canEdit = can(user, 'analytics.dashboard', 'update')
  const canCreate = can(user, 'analytics.dashboard', 'create')

  const create = useMutation({
    mutationFn: (name: string) =>
      createDashboard({ name, isShared: false, isDefault: false }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['dashboards'] })
      setSelected(created.id)
    },
  })
  const startCreate = () => {
    const name = window.prompt(t('dashboard.newName'))
    if (name?.trim()) create.mutate(name.trim())
  }

  return (
    <ReportPage
      titleKey="nav.dashboards"
      hintKey="dashboard.hint"
      controls={
        <>
          <select
            aria-label={t('dashboard.select')}
            value={selected ?? ''}
            onChange={(event) => setSelected(event.target.value || null)}
          >
            {(dashboards.data ?? []).map((dashboard) => (
              <option key={dashboard.id} value={dashboard.id}>
                {dashboard.name}
                {dashboard.isDefault ? ` ${t('dashboard.defaultMark')}` : ''}
              </option>
            ))}
          </select>
          {canCreate && (
            <button className="btn btn-secondary btn-sm" type="button" onClick={startCreate}>
              {t('dashboard.new')}
            </button>
          )}
          {canEdit && selected && (
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => setEditing((was) => !was)}>
              {editing ? t('dashboard.done') : t('dashboard.edit')}
            </button>
          )}
        </>
      }
    >
      {/* Сводка руководителя (макет Dashboard.dc.html) — над сохранёнными дашбордами:
          решения и картина компании нужны при входе, а свои дашборды из отчётов — ниже. */}
      <LeaderOverview />
      <h2 className="h2" style={{ marginTop: 8 }}>{t('dash.saved')}</h2>
      <ReportState
        isLoading={dashboards.isLoading || preferred.isLoading}
        error={dashboards.error}
        isEmpty={(dashboards.data ?? []).length === 0}
        empty={
          // #92: there is no period on this screen; the emptiness is "no dashboards yet",
          // and the answer to it is the button that makes the first one.
          <StateView
            compact
            kind="empty"
            title={t('dashboard.none.title')}
            description={canCreate ? t('dashboard.none.text') : t('dashboard.none.textReadOnly')}
            actions={
              canCreate ? (
                <button className="btn btn-primary btn-sm" type="button" onClick={startCreate}>
                  {t('dashboard.none.create')}
                </button>
              ) : undefined
            }
          />
        }
      >
        {selected && (
          <DashboardView
            dashboardId={selected}
            editing={editing}
            onEdit={canEdit ? () => setEditing(true) : undefined}
          />
        )}
      </ReportState>
    </ReportPage>
  )
}

/**
 * Один дашборд: сетка виджетов и, в режиме правки, конструктор.
 *
 * Автообновление — интервалом на весь дашборд, а не таймером на виджет. Минимальный
 * интервал среди виджетов задаёт частоту, и это осознанное огрубление: пять таймеров
 * дали бы пять запросов в разные моменты, то есть ту же нагрузку россыпью, которую
 * сложнее заметить в логе. Нижняя граница — минута, и её держит сервер.
 */
function DashboardView({
  dashboardId,
  editing,
  onEdit,
}: {
  dashboardId: string
  editing: boolean
  /** Opens the editor; absent without the right to edit. */
  onEdit?: () => void
}) {
  const { t } = useApp()
  const queryClient = useQueryClient()

  const dashboard = useQuery({
    queryKey: ['dashboard', dashboardId],
    queryFn: ({ signal }) => fetchDashboard(dashboardId, true, signal),
  })

  const widgets = dashboard.data?.widgets ?? []

  const refreshMs = useMemo(() => {
    if (widgets.length === 0) return false as const
    const fastest = Math.min(...widgets.map((w) => w.widget.refreshSeconds))
    return Math.max(fastest, MINIMUM_REFRESH_SECONDS) * 1000
  }, [widgets])

  // refetchInterval живёт отдельным эффектом, а не опцией запроса: интервал зависит от
  // ответа, а опция читается до него.
  useEffect(() => {
    if (refreshMs === false) return
    const timer = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] })
    }, refreshMs)
    return () => window.clearInterval(timer)
  }, [refreshMs, dashboardId, queryClient])

  const remove = useMutation({
    mutationFn: (widgetId: string) => deleteWidget(dashboardId, widgetId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] }),
  })

  // #94: drag changes the order; places are packed from it and saved for all widgets in
  // one request — a layout saved by halves is widgets on top of each other.
  const [dragged, setDragged] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)
  const [editingWidget, setEditingWidget] = useState<DashboardWidget | null>(null)
  const layout = useMutation({
    mutationFn: (ordered: DashboardWidget[]) => saveLayout(dashboardId, packLayout(ordered)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] }),
  })
  const reorder = (moved: string, target: string) => {
    const ordered = moveBefore(widgets.map((loaded) => loaded.widget), moved, target)
    if (ordered.map((widget) => widget.id).join() === widgets.map((loaded) => loaded.widget.id).join()) return
    layout.mutate(ordered)
  }
  // The drop is taken on the window: the pointer may be released over the gap between
  // cards, and a drag that never ends would leave the next click moving a widget.
  useEffect(() => {
    if (dragged === null) return
    const drop = () => {
      if (over !== null) reorder(dragged, over)
      setDragged(null)
      setOver(null)
    }
    window.addEventListener('pointerup', drop)
    return () => window.removeEventListener('pointerup', drop)
  })

  // One widget re-read on demand (#94): the whole dashboard would re-run every report
  // under it to refresh the one the viewer is looking at.
  const refresh = useMutation({
    mutationFn: (widgetId: string) => refreshWidget(dashboardId, widgetId),
    onSuccess: (fresh) =>
      queryClient.setQueryData<LoadedDashboard>(['dashboard', dashboardId], (was) =>
        was
          ? { ...was, widgets: was.widgets.map((loaded) => (loaded.widget.id === fresh.widget.id ? fresh : loaded)) }
          : was,
      ),
  })

  return (
    <ReportState
      isLoading={dashboard.isLoading}
      error={dashboard.error}
      isEmpty={!editing && widgets.length === 0}
      empty={
        <StateView
          compact
          kind="empty"
          title={t('dashboard.noWidgets.title')}
          description={onEdit ? t('dashboard.empty') : t('dashboard.noWidgets.textReadOnly')}
          actions={
            onEdit ? (
              <button className="btn btn-primary btn-sm" type="button" onClick={onEdit}>
                {t('dashboard.noWidgets.add')}
              </button>
            ) : undefined
          }
        />
      }
    >
      {editing && (
        <WidgetComposer
          dashboardId={dashboardId}
          dashboard={dashboard.data?.dashboard}
          editingWidget={editingWidget}
          onDoneEditing={() => setEditingWidget(null)}
        />
      )}
      {editing && widgets.length > 1 && <p className="muted t-sm">{t('dashboard.layout.hint')}</p>}
      {layout.error ? (
        <p role="alert" className="widget-error">{(layout.error as Error).message}</p>
      ) : null}

      <div className="dashboard-grid" style={{ gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)` }}>
        {widgets.map((loaded, index) => {
          const id = loaded.widget.id
          const neighbour = (step: number) => widgets[index + step]?.widget.id
          const before = neighbour(-1)
          const after = neighbour(1)
          return (
            <WidgetCard
              key={id}
              loaded={loaded}
              onRemove={editing ? () => remove.mutate(id) : undefined}
              drag={
                editing
                  ? {
                      onGrab: () => setDragged(id),
                      onHover: () => {
                        if (dragged !== null && dragged !== id) setOver(id)
                      },
                      dragging: dragged === id,
                      target: dragged !== null && over === id,
                    }
                  : undefined
              }
              actions={
                <>
                  <button
                    type="button"
                    className="link"
                    disabled={refresh.isPending && refresh.variables === id}
                    onClick={() => refresh.mutate(id)}
                  >
                    {t('dashboard.widget.refreshNow')}
                  </button>
                  {editing && (
                    <>
                      {/* Moving by buttons is the keyboard way to the same layout. */}
                      <button type="button" className="link" disabled={!before || layout.isPending}
                        aria-label={t('dashboard.widget.moveEarlier', { title: loaded.widget.title })}
                        onClick={() => before && reorder(id, before)}>
                        ←
                      </button>
                      <button type="button" className="link" disabled={!after || layout.isPending}
                        aria-label={t('dashboard.widget.moveLater', { title: loaded.widget.title })}
                        onClick={() => after && reorder(id, after)}>
                        →
                      </button>
                      <button type="button" className="link" onClick={() => setEditingWidget(loaded.widget)}>
                        {t('dashboard.widget.edit')}
                      </button>
                    </>
                  )}
                </>
              }
            />
          )
        })}
      </div>

      {widgets.length === 0 && editing && <p className="muted">{t('dashboard.empty')}</p>}
    </ReportState>
  )
}

/**
 * Конструктор: добавление и правка виджета поверх сохранённого отчёта и настройка дашборда.
 *
 * Источник выбирается **из сохранённых отчётов** (#76), а не вводится строкой: виджет
 * поверх несуществующего отчёта сервер и так отвергнет, но выбор из списка не даёт
 * ошибиться вовсе. Правка виджета (#94) — та же форма, заполненная им, и `PUT` на сервер:
 * размер, вид, источник и интервал меняются здесь, место — перетаскиванием.
 */
function WidgetComposer({
  dashboardId,
  dashboard,
  editingWidget,
  onDoneEditing,
}: {
  dashboardId: string
  dashboard: Dashboard | undefined
  editingWidget: DashboardWidget | null
  onDoneEditing: () => void
}) {
  const { t } = useApp()
  const queryClient = useQueryClient()

  const reports = useQuery({
    queryKey: ['reports'],
    queryFn: ({ signal }) => fetchReports(signal),
  })

  const save = useMutation({
    mutationFn: (patch: Partial<Dashboard>) =>
      updateDashboard(dashboardId, {
        name: patch.name ?? dashboard?.name ?? '',
        isShared: patch.isShared ?? dashboard?.isShared ?? false,
        isDefault: patch.isDefault ?? dashboard?.isDefault ?? false,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboards'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] })
    },
  })

  const drop = useMutation({
    mutationFn: () => deleteDashboard(dashboardId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dashboards'] }),
  })

  return (
    <section className="dashboard-composer">
      <h2>{editingWidget ? t('dashboard.widget.editTitle', { title: editingWidget.title }) : t('dashboard.composer')}</h2>

      {/* Keyed by the widget: switching from one widget to another starts from its own
          values, not from a half-typed draft of the previous one. */}
      <WidgetForm
        key={editingWidget?.id ?? 'new'}
        dashboardId={dashboardId}
        widget={editingWidget}
        reports={reports.data ?? []}
        onDone={onDoneEditing}
      />

      <div className="dashboard-settings">
        <label>
          <input
            type="checkbox"
            checked={dashboard?.isDefault ?? false}
            onChange={(event) => save.mutate({ isDefault: event.target.checked })}
          />
          {t('dashboard.isDefault')}
        </label>
        <label>
          <input
            type="checkbox"
            checked={dashboard?.isShared ?? false}
            onChange={(event) => save.mutate({ isShared: event.target.checked })}
          />
          {t('dashboard.isShared')}
        </label>
        <button type="button" className="danger" onClick={() => drop.mutate()}>
          {t('dashboard.delete')}
        </button>
      </div>
    </section>
  )
}

function WidgetForm({
  dashboardId,
  widget,
  reports,
  onDone,
}: {
  dashboardId: string
  widget: DashboardWidget | null
  reports: { id: string; name: string }[]
  onDone: () => void
}) {
  const { t } = useApp()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<SaveWidgetRequest>(() =>
    widget
      ? {
          title: widget.title,
          widgetType: widget.widgetType,
          dataSource: widget.dataSource,
          parameters: widget.parameters,
          gridX: widget.gridX,
          gridY: widget.gridY,
          gridWidth: widget.gridWidth,
          gridHeight: widget.gridHeight,
          refreshSeconds: widget.refreshSeconds,
        }
      : { title: '', widgetType: 'kpi', dataSource: '', gridWidth: 4, gridHeight: 3, refreshSeconds: 300 },
  )

  const submit = useMutation({
    mutationFn: () => (widget ? updateWidget(dashboardId, widget.id, draft) : addWidget(dashboardId, draft)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] })
      if (widget) onDone()
      else setDraft((was) => ({ ...was, title: '', dataSource: '' }))
    },
  })

  return (
    <>
      <form
        className="dashboard-widget-form"
        onSubmit={(event) => {
          event.preventDefault()
          submit.mutate()
        }}
      >
        <label>
          {t('dashboard.widget.title')}
          <input
            value={draft.title}
            onChange={(event) => setDraft({ ...draft, title: event.target.value })}
            required
          />
        </label>

        <label>
          {t('dashboard.widget.report')}
          <select
            value={draft.dataSource}
            onChange={(event) => setDraft({ ...draft, dataSource: event.target.value })}
            required
          >
            <option value="">{t('dashboard.widget.pickReport')}</option>
            {reports.map((report) => (
              <option key={report.id} value={report.id}>
                {report.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          {t('dashboard.widget.type')}
          <select
            value={draft.widgetType}
            onChange={(event) =>
              setDraft({ ...draft, widgetType: event.target.value as WidgetType })
            }
          >
            {WIDGET_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`dashboard.widget.type.${type}`)}
              </option>
            ))}
          </select>
        </label>

        <label>
          {t('dashboard.widget.width')}
          <input
            type="number"
            min={1}
            max={GRID_COLUMNS}
            value={draft.gridWidth}
            onChange={(event) =>
              setDraft({ ...draft, gridWidth: Number(event.target.value) })
            }
          />
        </label>

        <label>
          {t('dashboard.widget.height')}
          <input
            type="number"
            min={1}
            max={12}
            value={draft.gridHeight}
            onChange={(event) =>
              setDraft({ ...draft, gridHeight: Number(event.target.value) })
            }
          />
        </label>

        <label>
          {t('dashboard.widget.refresh')}
          <input
            type="number"
            min={MINIMUM_REFRESH_SECONDS}
            step={30}
            value={draft.refreshSeconds}
            onChange={(event) =>
              setDraft({ ...draft, refreshSeconds: Number(event.target.value) })
            }
          />
        </label>

        <button type="submit" disabled={!draft.title || !draft.dataSource || submit.isPending}>
          {widget ? t('dashboard.widget.save') : t('dashboard.widget.add')}
        </button>
        {widget && (
          <button type="button" className="link" onClick={onDone}>
            {t('dashboard.widget.cancelEdit')}
          </button>
        )}
      </form>

      {submit.error ? (
        <p role="alert" className="widget-error">
          {(submit.error as Error).message}
        </p>
      ) : null}
    </>
  )
}
